import { ExpandLess, KeyboardArrowRight } from '@mui/icons-material';
import { Box, CircularProgress, Collapse, Divider, IconButton, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import saveAs from 'file-saver';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import { ApplicationActions, slotNumberFromSlotName } from '../../../utils/constants';
import { migrateProject } from '../../../utils/projectMigrations';
import { parsePartialVersionString, parseProjectVersionString } from '../../../utils/projectVersions';
import { readSwerveSlotPythonSource } from '../../../utils/readSwerveSlotPythonSource';
import {
  getSaveSlotDisplayProgramName,
  isSaveSlotEmpty,
  parseBpsProjectMetadataFromPythonSource,
  parseProjectLastModifiedToUtcMs,
  projectLastModifiedUtcMsToIsoString,
  projectNameToDeviceFilename,
  saveSlotMatchesProject,
  stripBpsProjectIdFromDeployedPython,
} from '../../../utils/saveSlotProjectId';
import { createSwerveSlotBackupProject } from '../../../utils/swerveSlotBackupProject';
import ProgramManagementErrorDialog from '../../Dialogs/ErrorDialogs/ProgramManagementErrorDialog';
import MoveSlotDialog from '../../Dialogs/Project/MoveSlotDialog';
import MoveSlotOverwriteConfirmDialog from '../../Dialogs/Project/MoveSlotOverwriteConfirmDialog';
import PreviewPythonDialog from '../../Dialogs/Project/PreviewPythonDialog';
import ReplaceProgramConfirmDialog from '../../Dialogs/Project/ReplaceProgramConfirmDialog';
import UserProgramIcon from '../../Icons/Program/user-program.svg?react';
import SwerveSlotList from '../SwerveSlotList';
import SwerveSlotRow, { type SlotInfo } from '../SwerveSlotRow';

function swerveBackupFilename(slot: SlotInfo, deviceSlotId: string): string {
  const title = getSaveSlotDisplayProgramName(slot?.programTitle).trim();
  if (title) {
    return projectNameToDeviceFilename(title).replace(/\.py$/i, '.bps');
  }
  return `${deviceSlotId}.bps`;
}

interface SwerveSlotManagementPanelProps {
  isProgramRunning: boolean;
  isUserProgramsLoading: boolean;
  onDelete: (slotKey: string) => void;
  expanded: boolean;
  onToggle: () => void;
}

const SwerveSlotManagementPanel = ({ isProgramRunning, isUserProgramsLoading, onDelete, expanded, onToggle }: SwerveSlotManagementPanelProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const location = useLocation();
  const slotStorage = useSelector((state: RootState) => state.robotData.slotStorage) ?? {};
  const slotStorageFresh = useSelector((state: RootState) => state.robotData.slotStorageFresh);
  const loadedProject = useSelector((state: RootState) => state.project.loadedProject);
  const sessionSaveSlot = useSelector((state: RootState) => state.project.sessionSaveSlot);
  const panelExpanded = expanded && !isUserProgramsLoading;
  const userProgramsLoadingLabel = t('connectedDrawer.userPrograms.loading', 'Loading programs from the drive unit');
  const destructiveActionsDisabled = slotStorageFresh !== true;
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveOverwriteOpen, setMoveOverwriteOpen] = useState(false);
  const [moveSourceKey, setMoveSourceKey] = useState<string | null>(null);
  const [moveDestinationKey, setMoveDestinationKey] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [replaceProgramOpen, setReplaceProgramOpen] = useState(false);
  const [replaceSlotKey, setReplaceSlotKey] = useState<string | null>(null);
  const [isReplacingProgram, setIsReplacingProgram] = useState(false);
  const [previewPythonOpen, setPreviewPythonOpen] = useState(false);
  const [previewPythonSource, setPreviewPythonSource] = useState('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [programManagementErrorOpen, setProgramManagementErrorOpen] = useState(false);
  const canReplaceCurrentProgram = Boolean(loadedProject && !loadedProject.readOnly && location.pathname.startsWith('/editor/'));

  const resetMoveState = () => {
    setMoveDialogOpen(false);
    setMoveOverwriteOpen(false);
    setMoveSourceKey(null);
    setMoveDestinationKey(null);
  };

  const handleMoveError = (error: unknown) => {
    console.error('Failed to move program between Drive Unit slots', error);
    resetMoveState();
    setProgramManagementErrorOpen(true);
  };

  const executeMove = async () => {
    if (moveSourceKey === null || moveDestinationKey === null || isMoving || isProgramRunning) return;

    setIsMoving(true);
    try {
      const sourceSlot = slotStorage[moveSourceKey];
      const currentSelectedSlot = sessionSaveSlot;
      const loadedProjectLastModifiedUtcMs = parseProjectLastModifiedToUtcMs(loadedProject?.lastModified);
      const sourceSlotNumber = slotNumberFromSlotName(moveSourceKey);
      const movedCurrentProject =
        saveSlotMatchesProject(sourceSlot, loadedProject?.id, loadedProjectLastModifiedUtcMs) ||
        Boolean(loadedProject?.id && sourceSlot?.bpsProjectId === loadedProject.id && currentSelectedSlot === sourceSlotNumber) ||
        (!sourceSlot?.bpsProjectId && currentSelectedSlot === sourceSlotNumber);
      const movePromise = window.robotService?.movePythonProgram?.(moveSourceKey, moveDestinationKey);
      if (!movePromise) {
        throw new Error('movePythonProgram unavailable');
      }

      await movePromise;
      if (movedCurrentProject) {
        dispatch(projectSlice.actions.setSelectedSaveSlot(slotNumberFromSlotName(moveDestinationKey)));
      }
      resetMoveState();
    } catch (error) {
      handleMoveError(error);
    } finally {
      setIsMoving(false);
    }
  };

  const requestMoveToSlot = (sourceKey: string) => {
    setMoveSourceKey(sourceKey);
    setMoveDestinationKey(null);
    setMoveOverwriteOpen(false);
    setMoveDialogOpen(true);
  };

  const handleMoveDialogConfirm = () => {
    if (moveDestinationKey === null || isProgramRunning) return;

    if (isSaveSlotEmpty(slotStorage[moveDestinationKey])) {
      void executeMove();
      return;
    }

    setMoveDialogOpen(false);
    setMoveOverwriteOpen(true);
  };

  const handleCancelMoveDialog = () => {
    if (isMoving) return;
    resetMoveState();
  };

  const handleCancelOverwriteDialog = () => {
    if (isMoving) return;
    setMoveOverwriteOpen(false);
    setMoveDialogOpen(true);
  };

  const resetReplaceProgramState = () => {
    setReplaceProgramOpen(false);
    setReplaceSlotKey(null);
  };

  const requestReplaceCurrentProgram = (slotKey: string) => {
    if (!canReplaceCurrentProgram || isProgramRunning) return;
    setReplaceSlotKey(slotKey);
    setReplaceProgramOpen(true);
  };

  const handleConfirmReplaceCurrentProgram = () => {
    if (!canReplaceCurrentProgram || replaceSlotKey === null || isReplacingProgram || isProgramRunning) return;
    const replacementProjectId = loadedProject?.id;
    if (!replacementProjectId) return;

    setIsReplacingProgram(true);
    void (async () => {
      try {
        const sourceSlot = slotStorage[replaceSlotKey];
        const pythonSource = await readSwerveSlotPythonSource(replaceSlotKey);
        const parsedProjectMetadata = parseBpsProjectMetadataFromPythonSource(pythonSource);
        const replacementLastModified = projectLastModifiedUtcMsToIsoString(
          parsedProjectMetadata.projectLastModifiedUtcMs ?? sourceSlot?.bpsProjectLastModifiedUtcMs
        );
        dispatch(projectSlice.actions.onSaveLayout(parsedProjectMetadata.dashboardLayout ?? []));
        // Migrate the slot's path-builder metadata exactly once here, keyed on the deployed footer's project
        // version, using the same version-gated owner (migrateProject) as project load. rehydratePathBuilderState
        // then trusts this input and does not migrate again, so value transforms can never run more than once.
        // A missing footer version falls through to migrateProject's legacy baseline (1.0.0), upgrading old slots.
        const slotProjectVersion =
          parseProjectVersionString(parsedProjectMetadata.projectVersion) ??
          parsePartialVersionString(parsedProjectMetadata.projectVersion) ??
          undefined;
        const migratedPathState = migrateProject({
          version: slotProjectVersion,
          paths: parsedProjectMetadata.paths,
          selectedPathId: parsedProjectMetadata.selectedPathId,
          pathBuilderFieldRotation: parsedProjectMetadata.pathBuilderFieldRotation,
        });
        // Rehydrate path-builder state from the slot, mirroring dashboard behavior: fields absent from the
        // deploy metadata reset to defaults (paths cleared, default field/preview mode, rotation 0).
        dispatch(
          projectSlice.actions.rehydratePathBuilderState({
            paths: migratedPathState.paths,
            selectedPathId: migratedPathState.selectedPathId,
            selectedField: parsedProjectMetadata.selectedField,
            pathPreviewMode: parsedProjectMetadata.pathPreviewMode,
            pathBuilderFieldRotation: migratedPathState.pathBuilderFieldRotation,
          })
        );
        dispatch(
          projectSlice.actions.queueProgramReplacement({
            projectId: replacementProjectId,
            code: stripBpsProjectIdFromDeployedPython(pythonSource),
            lastModified: replacementLastModified,
          })
        );
        resetReplaceProgramState();
      } catch (error) {
        console.error('Failed to replace current program from Drive Unit slot', error);
        resetReplaceProgramState();
        setProgramManagementErrorOpen(true);
      } finally {
        setIsReplacingProgram(false);
      }
    })();
  };

  const handlePreviewPython = (slotKey: string) => {
    if (isPreviewLoading || isProgramRunning) return;

    void (async () => {
      setIsPreviewLoading(true);
      try {
        const pythonSource = await readSwerveSlotPythonSource(slotKey);
        setPreviewPythonSource(stripBpsProjectIdFromDeployedPython(pythonSource));
        setPreviewPythonOpen(true);
      } catch (error) {
        console.error('Failed to preview Python from Drive Unit slot', error);
        setPreviewPythonOpen(false);
        setPreviewPythonSource('');
        setProgramManagementErrorOpen(true);
      } finally {
        setIsPreviewLoading(false);
      }
    })();
  };

  const handleClosePreviewPython = () => {
    setPreviewPythonOpen(false);
    setPreviewPythonSource('');
  };

  const handleOnSaveAsNew = (slotKey: string, slot: SlotInfo) => {
    if (isProgramRunning) return;

    dispatch(
      applicationSlice.actions.onOpenDialog({
        ...ApplicationActions.Dialogs.PROJECT_NAME,
        options: {
          ...ApplicationActions.Dialogs.PROJECT_NAME.options,
          title: 'projectNameDialog.new.title',
          saveFromSwerveSlot: true,
          swerveSlotAssetId: slotKey,
          swerveSlotProgramTitle: slot?.programTitle,
          swerveSlotBpsProjectId: slot?.bpsProjectId,
          swerveSlotBpsProjectLastModifiedUtcMs: slot?.bpsProjectLastModifiedUtcMs,
        },
      })
    );
  };

  const handleOnBackupToDevice = async (slotKey: string, slot: SlotInfo) => {
    if (isProgramRunning) return;

    const pythonSource = await readSwerveSlotPythonSource(slotKey);
    const backupProject = await createSwerveSlotBackupProject({
      pythonSource,
      slot,
      fallbackName: slotKey,
    });
    const fileName = swerveBackupFilename(slot, slotKey);
    const blob = new Blob([JSON.stringify(backupProject, null, 2)], { type: 'application/json;charset=utf-8' });
    saveAs(blob, fileName);
  };

  return (
    <>
      <Paper elevation={2} sx={{ marginTop: '17px', marginBottom: '17px' }} aria-busy={isUserProgramsLoading}>
        <Box
          onClick={isUserProgramsLoading ? undefined : onToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 24px',
            cursor: isUserProgramsLoading ? 'default' : 'pointer',
            '&:hover': { bgcolor: isUserProgramsLoading ? undefined : 'action.hover' },
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
            <Box
              component={UserProgramIcon}
              aria-hidden
              sx={{
                width: 22,
                height: 22,
                display: 'block',
                color: 'text.primary',
                flexShrink: 0,
              }}
            />
            <Typography variant="subHeaderTitle" sx={{ fontSize: isSmallScreen ? '16px' : isMediumScreen ? '18px' : '20px' }}>
              <Trans i18nKey="connectedDrawer.user-programs.title">User Programs</Trans>
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
            {isUserProgramsLoading ? <CircularProgress size={20} aria-label={userProgramsLoadingLabel} /> : null}
            <IconButton
              size="small"
              disabled={isUserProgramsLoading}
              aria-label={panelExpanded ? t('aria-label.collapse', 'collapse') : t('aria-label.expand', 'expand')}
            >
              {panelExpanded ? <ExpandLess /> : <KeyboardArrowRight />}
            </IconButton>
          </Stack>
        </Box>
        <Collapse in={panelExpanded}>
          <Box padding="24px" paddingTop={0}>
            <Divider />
            <SwerveSlotList
              renderSlot={(slot, slotKey) => (
                <SwerveSlotRow
                  slotNumber={slotNumberFromSlotName(slotKey)}
                  slot={slot}
                  disableActions={isProgramRunning}
                  destructiveActionsDisabled={destructiveActionsDisabled}
                  replaceProgramDisabled={!canReplaceCurrentProgram}
                  moveProgramDisabled={false}
                  onPreviewPython={() => {
                    handlePreviewPython(slotKey);
                  }}
                  onReplaceProgram={() => {
                    requestReplaceCurrentProgram(slotKey);
                  }}
                  onMoveToSlot={() => {
                    requestMoveToSlot(slotKey);
                  }}
                  onSaveAsNew={() => {
                    handleOnSaveAsNew(slotKey, slot);
                  }}
                  onBackupToDevice={() => {
                    return handleOnBackupToDevice(slotKey, slot);
                  }}
                  onDelete={() => {
                    onDelete(slotKey);
                  }}
                />
              )}
            />
          </Box>
        </Collapse>
      </Paper>
      <MoveSlotDialog
        open={moveDialogOpen}
        isMoving={isMoving}
        confirmDisabled={isProgramRunning}
        selectedDestinationKey={moveDestinationKey}
        slotStorage={slotStorage}
        sourceSlotKey={moveSourceKey}
        onCancel={handleCancelMoveDialog}
        onConfirm={handleMoveDialogConfirm}
        onSelectDestination={setMoveDestinationKey}
      />
      <MoveSlotOverwriteConfirmDialog
        open={moveOverwriteOpen}
        confirmDisabled={isProgramRunning}
        destinationSlot={moveDestinationKey === null ? null : slotStorage[moveDestinationKey]}
        destinationSlotNumber={moveDestinationKey === null ? null : slotNumberFromSlotName(moveDestinationKey)}
        isMoving={isMoving}
        onCancel={handleCancelOverwriteDialog}
        onConfirm={() => {
          void executeMove();
        }}
      />
      <ReplaceProgramConfirmDialog
        open={replaceProgramOpen}
        isReplacing={isReplacingProgram}
        confirmDisabled={isProgramRunning}
        onCancel={resetReplaceProgramState}
        onConfirm={handleConfirmReplaceCurrentProgram}
      />
      <PreviewPythonDialog open={previewPythonOpen} pythonSource={previewPythonSource} onClose={handleClosePreviewPython} />
      <ProgramManagementErrorDialog open={programManagementErrorOpen} onClose={() => setProgramManagementErrorOpen(false)} />
    </>
  );
};

export default SwerveSlotManagementPanel;
